import { DEFAULT_COMBAT_STATS } from '../constants.js';
import { normalizeTeamId } from '../utils.js';

export function cloneHpSnapshot(hpState = []) {
  if (!Array.isArray(hpState)) return [];
  return hpState.map(item => ({ ...item }));
}

function deriveOwnerName(metadata = {}, preview = null, fallbackUserId = null) {
  let name = metadata.playerName
    || metadata.username
    || metadata.userName
    || metadata.owner
    || metadata.ownerName
    || metadata.player
    || metadata.telegram_username
    || metadata.telegramUsername
    || null;
  if (!name && metadata.user && typeof metadata.user === 'object') {
    name = metadata.user.username || metadata.user.name || metadata.user.fullName || null;
  }
  if (!name && preview?.owner) {
    name = preview.owner;
  }
  if (!name && fallbackUserId) {
    name = `Игрок #${fallbackUserId}`;
  }
  return name;
}

export function buildBattlePanels(battle, { playerPreview = null, enemyPreview = null } = {}) {
  if (!battle || !Array.isArray(battle.combatants)) {
    return [];
  }

  const previewsByTeam = {
    A: playerPreview,
    B: enemyPreview
  };

  const rawEntries = battle.combatants.map((combatant, index) => ({
    combatant,
    index,
    id: combatant.id ?? index + 1
  }));
  const rawById = new Map(rawEntries.map(entry => [entry.id, entry]));

  return battle.combatants.map((combatant, index) => {
    const id = combatant.id ?? index + 1;
    const team = (combatant.team || (index === 0 ? 'A' : 'B'));
    const upperTeam = String(team).toUpperCase();
    const preview = previewsByTeam[upperTeam] || null;
    const state = combatant.state || {};
    const base = state.base || {};
    const current = state.current || {};
    const maxHp = Math.max(1, base.hp ?? current.hp ?? DEFAULT_COMBAT_STATS.hp);
    const types = Array.isArray(state.types) ? state.types : (preview?.types || []);
    const metadata = combatant.metadata || {};

    let ownerName = deriveOwnerName(metadata, preview, combatant.userId ?? null);
    // Если ownerName не найден, но есть userId, это реальный игрок (не случайный враг)
    // В этом случае используем fallback "Игрок #userId" вместо null
    if (!ownerName && combatant.userId) {
      ownerName = `Игрок #${combatant.userId}`;
    }
    
    // Формируем имя существа: сначала из displayName (реальное имя из БД), потом из preview, потом из baseId
    // displayName имеет приоритет, так как содержит реальное имя существа из данных
    let creatureName = metadata.displayName || preview?.name || null;
    
    // Если имя не найдено, но есть baseId, используем его (это ID существа из данных)
    if (!creatureName && metadata.baseId) {
      creatureName = `Существо #${metadata.baseId}`;
    }
    
    // Последний fallback - ID бойца
    if (!creatureName) {
      creatureName = `#${id}`;
    }
    
    let name = creatureName;
    let subtitle = preview?.subtitle || `Команда ${upperTeam}`;

    const summonTags = Array.isArray(metadata.summonTags) ? metadata.summonTags.map(String) : [];
    const summonOwnerId = metadata.ownerId ?? metadata.cloneOf ?? metadata.summonSourceId ?? null;
    const ownerEntry = summonOwnerId != null ? rawById.get(summonOwnerId)?.combatant : null;
    const ownerPreview = ownerEntry ? previewsByTeam[String(ownerEntry.team || '').toUpperCase()] || null : null;
    if (!ownerName && ownerEntry) {
      ownerName = deriveOwnerName(ownerEntry.metadata || {}, ownerPreview, ownerEntry.userId ?? null);
    }

    const isSummon = Boolean(
      metadata.summon
      || metadata.isoMirrorClone
      || metadata.forkClone
      || summonTags.length
      || metadata.summonSourceId != null
      || metadata.cloneOf != null
    );

    let summonInfo = null;
    if (isSummon) {
      const abilityId = metadata.abilityId ?? metadata.summonAbilityId ?? null;
      summonInfo = {
        ownerId: summonOwnerId,
        sourceId: metadata.summonSourceId ?? metadata.cloneOf ?? null,
        abilityId,
        createdAt: metadata.summonCreatedAt ?? null,
        tags: summonTags,
        cloneOf: metadata.cloneOf ?? null
      };

      const ownerLabelEntry = ownerEntry ? deriveOwnerName(ownerEntry.metadata || {}, ownerPreview, ownerEntry.userId ?? null) : null;
      const ownerLabel = ownerLabelEntry || ownerName || null;
      if (ownerLabel && !ownerName) {
        ownerName = ownerLabel;
      }

      if (!metadata.displayName) {
        const isMirrorClone = summonTags.includes('iso_clone') || metadata.isoMirrorClone;
        const isProcessClone = summonTags.includes('fork_clone') || metadata.forkClone;
        if (isMirrorClone) {
          name = ownerLabel ? `Зеркальный клон ${ownerLabel}` : 'Зеркальный клон';
        } else if (isProcessClone) {
          name = ownerLabel ? `Процесс-клон ${ownerLabel}` : 'Процесс-клон';
        } else {
          name = ownerLabel ? `Призванный юнит ${ownerLabel}` : 'Призванный юнит';
        }
      }
      subtitle = ownerLabel ? `Источник: ${ownerLabel}` : 'Призванный юнит';
    }

    return {
      id,
      team: upperTeam,
      name,
      subtitle,
      owner: ownerName || null,
      avatar: preview?.image || null,
      types,
      maxHp,
      highlight: preview?.highlight || '',
      highlightTitle: preview?.highlightTitle || '',
      raw: combatant,
      isSummon,
      summonInfo
    };
  });
}

export function getPanelById(panels, id) {
  if (!Array.isArray(panels)) return null;
  return panels.find(panel => panel.id === id) || null;
}

export function getPanelByTeam(panels, team) {
  if (!Array.isArray(panels)) return null;
  const upper = team ? String(team).toUpperCase() : null;
  if (!upper) return null;
  return panels.find(panel => String(panel.team).toUpperCase() === upper) || null;
}

export function determineWinnerInfo(battle, panels, timeline = [], summary = {}, computeWinnerFn = () => null) {
  let team = normalizeTeamId(summary.winner || summary.winnerTeam || summary.winner_team || summary.team || summary.teamWinner || summary.team_winner);

  if (!team && Array.isArray(timeline) && timeline.length > 0) {
    const lastSnapshot = timeline[timeline.length - 1]?.snapshot || [];
    if (lastSnapshot.length) {
      const teamsAlive = new Set();
      lastSnapshot.forEach(item => {
        if (!item || item.hp == null || item.hp <= 0) return;
        const panel = getPanelById(panels, item.id);
        if (panel?.team) {
          teamsAlive.add(String(panel.team).toUpperCase());
        }
      });
      if (teamsAlive.size === 1) {
        team = [...teamsAlive][0];
      }
    }
  }

  if (!team && typeof computeWinnerFn === 'function' && battle) {
    team = normalizeTeamId(computeWinnerFn(battle));
  }

  if (!team) {
    return {
      team: null,
      owner: 'Ничья',
      name: ''
    };
  }

  const panel = getPanelByTeam(panels, team);
  const owner = panel?.owner || `Команда ${team}`;
  const name = panel?.name || '';
  return {
    team,
    owner,
    name
  };
}
